import { DayPicker } from "react-day-picker";
import { CSSTransition } from "react-transition-group";

import styled, { css } from "styled-components";
import tw from "twin.macro";

import { BoxSizingStyle } from "../BoxSizingStyle";
import { FontStyle } from "../FontStyle";

export const StyledDatePicker = styled.div`
  ${FontStyle}
  ${BoxSizingStyle}

  overflow: hidden;
  min-width: 230px;

  ${tw`tw-flex tw-justify-center`}
`;

export const StyledDayPicker = styled(DayPicker)<{ $isYearlyView: boolean }>`
  ${tw`tw-bg-page-paper-main`}
  width: 230px;

  .root {
    --rdp-cell-size: 40px; /* Size of the day cells. */
    --rdp-caption-font-size: 18px; /* Font size for the caption labels. */
    --rdp-accent-color: #0000ff; /* Accent color for the background of selected days. */
    --rdp-background-color: #e7edff; /* Background color for the hovered/focused elements. */
    --rdp-accent-color-dark: #3003e1; /* Accent color for the background of selected days (to use in dark-mode). */
    --rdp-background-color-dark: #180270; /* Background color for the hovered/focused elements (to use in dark-mode). */
    --rdp-outline: 2px solid var(--rdp-accent-color); /* Outline border for focused elements */
    --rdp-outline-selected: 3px solid var(--rdp-accent-color); /* Outline border for focused _and_ selected elements */
    --rdp-selected-color: #fff; /* Color of selected day text */

    margin: 1em;
  }

  /* Hide elements for devices that are not screen readers */
  .rdp-vhidden {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    background: transparent;
    border: 0;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    position: absolute !important;
    top: 0;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    overflow: hidden !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    border: 0 !important;
  }

  /* Buttons */
  .rdp-button_reset {
    appearance: none;
    position: relative;
    margin: 0;
    padding: 0;
    cursor: default;
    color: inherit;
    background: none;
    font: inherit;

    -moz-appearance: none;
    -webkit-appearance: none;
  }

  .rdp-button_reset:focus-visible {
    /* Make sure to reset outline only when :focus-visible is supported */
    outline: none;
  }

  .rdp-button {
    border: 2px solid transparent;
  }

  .rdp-button[disabled]:not(.rdp-day_selected) {
    opacity: 0.25;
  }

  .rdp-button:not([disabled]) {
    cursor: pointer;
  }

  .rdp-button:focus-visible:not([disabled]) {
    color: inherit;
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
  }

  .rdp-months {
    display: flex;
  }

  .rdp-month {
    margin: 0 1em;
  }

  .rdp-month:first-child {
    margin-left: 0;
  }

  .rdp-month:last-child {
    margin-right: 0;
  }

  .rdp-table {
    margin: 0;
    max-width: calc(var(--rdp-cell-size) * 7);
    border-collapse: collapse;
  }

  .rdp-with_weeknumber .table {
    max-width: calc(var(--rdp-cell-size) * 8);
    border-collapse: collapse;
  }

  .rdp-caption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
    text-align: left;
  }

  .rdp-multiple_months .rdp-caption {
    position: relative;
    display: block;
    text-align: center;
  }

  .rdp-caption_dropdowns {
    position: relative;
    display: inline-flex;
  }

  .rdp-caption_label {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    margin: 0;
    padding: 0 0.25em;
    white-space: nowrap;
    color: currentColor;
    border: 0;
    border: 2px solid transparent;
    font-family: inherit;
    font-size: var(--rdp-caption-font-size);
    font-weight: bold;
  }

  .rdp-nav {
    white-space: nowrap;
  }

  .rdp-multiple_months .rdp-caption_start .rdp-nav {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }

  .rdp-multiple_months .rdp-caption_end .rdp-nav {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
  }

  .rdp-nav_button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    padding: 0.25em;
    border-radius: 100%;
  }

  /* ---------- */
  /* Dropdowns  */
  /* ---------- */

  .rdp-dropdown_year,
  .rdp-dropdown_month {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .rdp-dropdown {
    appearance: none;
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    cursor: inherit;
    opacity: 0;
    border: none;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .rdp-dropdown[disabled] {
    opacity: unset;
    color: unset;
  }

  .rdp-dropdown:focus-visible:not([disabled]) + .rdp-caption_label {
    background-color: var(--rdp-background-color);
    border: var(--rdp-outline);
    border-radius: 6px;
  }

  .rdp-dropdown_icon {
    margin: 0 0 0 5px;
  }

  .rdp-head {
    border: 0;
  }

  .rdp-head_row,
  .rdp-row {
    height: 100%;
  }

  .rdp-head_cell {
    vertical-align: middle;
    font-size: 0.75em;
    font-weight: 700;
    text-align: center;
    height: 100%;
    height: var(--rdp-cell-size);
    padding: 0;
    text-transform: uppercase;
  }

  .rdp-tbody {
    border: 0;
  }

  .rdp-tfoot {
    margin: 0.5em;
  }

  .rdp-cell {
    width: var(--rdp-cell-size);
    height: 100%;
    height: var(--rdp-cell-size);
    padding: 0;
    text-align: center;
  }

  .rdp-weeknumber {
    font-size: 0.75em;
  }

  .rdp-weeknumber,
  .rdp-day {
    display: flex;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: var(--rdp-cell-size);
    max-width: var(--rdp-cell-size);
    height: var(--rdp-cell-size);
    margin: 0;
    border: 2px solid transparent;
    border-radius: 100%;
  }

  .rdp-day_today:not(.rdp-day_outside) {
    font-weight: bold;
  }

  .rdp-day_selected,
  .rdp-day_selected:focus-visible {
    color: var(--rdp-selected-color);
    opacity: 1;
    background-color: var(--rdp-accent-color);
  }

  .rdp-day_outside {
    opacity: 0.5;
  }

  .rdp-day_selected:focus-visible {
    /* Since the background is the same use again the outline */
    outline: var(--rdp-outline);
    outline-offset: 2px;
    z-index: 1;
  }

  .root:not([dir="rtl"]) .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .root:not([dir="rtl"]) .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .root[dir="rtl"] .rdp-day_range_start:not(.rdp-day_range_end) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .root[dir="rtl"] .rdp-day_range_end:not(.rdp-day_range_start) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .rdp-day_range_end.rdp-day_range_start {
    border-radius: 100%;
  }

  .rdp-day_range_middle {
    border-radius: 0;
  }

  ${(props) =>
    props.$isYearlyView &&
    css`
      .c-DayPicker-Weekdays,
      .c-DayPicker-Body {
        opacity: 0 !important;
      }
    `}

  .c-DayPicker-Month {
    display: table;
    user-select: none;
    border-collapse: separate;
    border-spacing: 4px;
    margin: 0 auto;
  }

  .c-DayPicker-wrapper {
    display: flex;
  }

  .c-DayPicker-Wrapper {
    position: relative;
    flex-direction: row;
    user-select: none;
    width: 100%;
  }

  .c-DayPicker-Caption {
    display: table-caption;
    margin-bottom: 0.5em;
    padding: 0 0.5em;
    width: 100%;
    ${tw`tw-text-center tw-text-theme-900`}
  }

  .c-DayPicker-Weekday {
    font-size: 11px;
    font-weight: normal;
    padding: 0.5em;
    text-align: center;
    ${tw`tw-text-theme-600`}
  }

  .c-DayPicker-Body {
    display: table-row-group;
    font-size: 13px;
    ${tw`tw-text-theme-900 tw-font-bold`}
  }

  .c-DayPicker-Day {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    cursor: pointer;
    transition-duration: 0.3s;
    width: 27px;
    height: 27px;
    line-height: 27px;
  }

  .c-DayPicker {
    display: inline-block;
    font-size: 1rem;
  }

  .c-DayPicker-Months {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .c-DayPicker-NavButton--interactionDisabled {
    display: none;
  }

  .c-DayPicker-Weekdays {
    text-decoration: none;
    display: table-header-group;
    margin-top: 1em;
  }

  .c-DayPicker-WeekdaysRow {
    display: table-row;
  }

  .c-DayPicker-Week {
    display: table-row;
  }

  .c-DayPicker--interactionDisabled .c-DayPicker-Day {
    cursor: default;
  }

  .hovered.hovered-day.hover:not(
      .c-DayPicker-Day--selected.selected-day.selected
    ) {
    background-color: var(--color-primary-300);
    color: var(--color-theme-900);
  }

  .c-DayPicker-Day--today {
    font-weight: 700;
    color: var(--color-primary);
    position: relative;

    &:after {
      position: absolute;
      content: "";
      bottom: 1px;
      height: 3px;
      width: 3px;
      border-radius: 3px;
      margin: 0 auto;
      left: 0;
      right: 0;
      background: var(--color-primary);
    }
    &.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      color: var(--color-primary);
      &:after {
        background-color: var(--color-primary);
      }
    }
  }

  .c-DayPicker-Day--userAvailability,
  .c-DayPicker-Day--nonWorkingDaysOfWeek {
    color: var(--red-alert);
    &.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      background-color: rgba(237, 97, 97, 0.1);
      color: var(--red-alert);
    }
    &.c-DayPicker-Day--today {
      color: var(--red-alert);
      &:after {
        background-color: var(--red-alert);
      }
      &.hovered.hovered-day.hover:not(
          .c-DayPicker-Day--selected.selected-day.selected
        ):after {
        background-color: var(--red-alert);
      }
    }
  }

  .c-DayPicker-Day--selected.selected-day.selected {
    background-color: var(--color-primary);
    color: var(--color-theme-100);
    &.c-DayPicker-Day--today {
      color: var(--color-theme-100);
      &:after {
        background-color: var(--color-theme-100);
      }
    }
    &.c-DayPicker-Day--userAvailability,
    &.c-DayPicker-Day--nonWorkingDaysOfWeek {
      background-color: var(--red-alert);
      color: var(--color-theme-100);
    }
  }

  .c-DayPicker-Day--nonWorkingDay {
    color: var(--color-theme-500);
    &.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      color: var(--color-theme-500);
    }
    &.c-DayPicker-Day--today {
      color: var(--color-primary-500);
      &:after {
        background-color: var(--color-primary-500);
      }
      &.hovered.hovered-day.hover:not(
          .c-DayPicker-Day--selected.selected-day.selected
        ) {
        color: var(--color-theme-500);
        &:after {
          background-color: var(--color-theme-500);
        }
      }
    }
    &.c-DayPicker-Day--userAvailability,
    &.c-DayPicker-Day--nonWorkingDaysOfWeek {
      &.hovered.hovered-day.hover:not(
          .c-DayPicker-Day--selected.selected-day.selected
        ) {
        color: var(--color-theme-500);
        background-color: var(--color-primary-300);
        &.c-DayPicker-Day--today:after {
          background-color: var(--color-theme-500);
        }
      }
    }
    &.c-DayPicker-Day--selected.selected-day.selected {
      color: rgba(255, 255, 255, 0.6);
      background-color: var(--color-primary);
      .neon & {
        color: rgba(0, 0, 0, 0.4);
      }
      &.c-DayPicker-Day--today {
        color: rgba(255, 255, 255, 0.6);
        .neon & {
          color: rgba(0, 0, 0, 0.4);
        }
        &:after {
          background-color: rgba(255, 255, 255, 0.6);
          .neon & {
            background-color: rgba(0, 0, 0, 0.4);
          }
        }
      }
    }
  }
  .c-DayPicker-Day--disabled {
    cursor: default;
    color: var(--color-theme-500);
    &.hovered.hovered-day.hover {
      color: var(--color-theme-500);
    }
  }

  .c-DayPicker-Day--outside {
    cursor: default;
    opacity: 0.15;
    color: var(--color-theme-900);

    &.c-DayPicker-Day--userAvailability.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--nonWorkingDaysOfWeek.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      background: var(--color-primary-300);
    }
    &.c-DayPicker-Day--userAvailability:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--nonWorkingDaysOfWeek:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--userAvailability.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--nonWorkingDaysOfWeek.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--nonWorkingDay.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--nonWorkingDay:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--today.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ),
    &.c-DayPicker-Day--today:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      color: var(--color-theme-900);
    }

    &.c-DayPicker-Day--userAvailability.c-DayPicker-Day--today:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--nonWorkingDaysOfWeek.c-DayPicker-Day--today:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--userAvailability.c-DayPicker-Day--today.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--nonWorkingDaysOfWeek.c-DayPicker-Day--today.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--nonWorkingDay.c-DayPicker-Day--today:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--nonWorkingDay.c-DayPicker-Day--today.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--today.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after,
    &.c-DayPicker-Day--today:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ):after {
      background-color: var(--color-theme-900);
    }

    &.hovered.hovered-day.hover:not(
        .c-DayPicker-Day--selected.selected-day.selected
      ) {
      background: var(--color-primary-400);
    }

    &.c-DayPicker-Day--selected.selected-day.selected {
      color: var(--page-paper-main);
      background: var(--color-primary);
      .neon & {
        color: var(--page-paper-main);
      }
      &.c-DayPicker-Day--today {
        &:after {
          background: var(--page-paper-main);
          .neon & {
            background: var(--page-paper-main);
          }
        }
        &.c-DayPicker-Day--nonWorkingDay {
          color: var(--page-paper-main);
          .neon & {
            color: var(--page-paper-main);
          }
        }
      }
    }
  }
`;

export const StyledDatePickerCSSTransition = styled(CSSTransition)<{
  $direction: boolean;
}>`
  ${(props) =>
    props.$direction
      ? css`
          &.c-date-picker__animation-enter {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 0;
              transform: translateY(-20px);
            }
          }

          &.c-date-picker__animation-enter-active {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 1;
              transform: translateY(0px);
              transition-duration: 300ms;
            }
          }
          &.c-date-picker__animation-exit {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 1;
            }
          }
          &.c-date-picker__animation-exit-active {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 0;
              transform: translateY(20px);
              transition-duration: 300ms;
            }
          }
        `
      : css`
          &.c-date-picker__animation-enter {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 0;
              transform: translateY(20px);
            }
          }

          &.c-date-picker__animation-enter-active {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 1;
              transform: translateY(0px);
              transition-duration: 300ms;
            }
          }

          &.c-date-picker__animation-exit {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 1;
            }
          }

          &.c-date-picker__animation-exit-active {
            .c-DayPicker-Caption,
            .c-DayPicker-Body {
              opacity: 0;
              transform: translateY(-20px);
              transition-duration: 300ms;
            }
          }
        `}
`;

StyledDatePickerCSSTransition.displayName = "StyledDatePickerCSSTransition";
StyledDatePicker.displayName = "StyledDatePicker";
StyledDayPicker.displayName = "StyledDayPicker";
